// The onboarding wizard's options: one build question, with a framework probe
// under the SIP and audio streaming options.

import { REGIONS } from 'v2/configs/constants'
import type { BuildChoice, Framework, WizardState } from 'v2/shared/onboarding/types'
import { getLocalAuth } from 'v2/utils'
import { BuildGoal, type OnboardingFormData } from '../types'

// Core rejects '<' and '>' in any string field, so free text is stripped of them.
export const stripUnsafeChars = (value: string): string => value.replace(/[<>]/g, '')

export { INITIAL_WIZARD_STATE } from 'v2/shared/onboarding/types'
export type { BuildChoice, Framework, WizardState }

export interface BuildOption {
  value: BuildChoice
  title: string
  description: string
  // 'enterprise' marks a restriction and gets the lock; 'neutral' names a product.
  badge?: { label: string; tone: 'enterprise' | 'neutral'; tooltip?: string }
}

// Region-independent options. SIP and messaging vary by region, so they live in buildOptions.
const VOICE_AI: BuildOption = {
  value: 'voice-ai',
  title: 'No-code Voice Agent',
  description: 'Build and launch a voice agent in our visual builder. No code needed.',
  badge: { label: 'AI Agent Studio', tone: 'neutral' },
}

const AUDIO: BuildOption = {
  value: 'audio',
  title: 'Build Your Voice Agent Programmatically',
  description: 'Stream live call audio to your server and orchestrate any STT, LLM, and TTS.',
  badge: { label: 'WebSocket, Audio Streaming', tone: 'neutral' },
}

const VOICE_API: BuildOption = {
  value: 'voice-api',
  title: 'Add Human Voice Calling to Your Applications',
  description: 'Enable call forwarding, call conferencing, or click-to-call within your app.',
  badge: { label: 'Plivo XML', tone: 'neutral' },
}

// A function, not a const: the region comes from localStorage, which is empty at
// module load on a fresh signup.
export const buildOptions = (): BuildOption[] => {
  const isIndia = getLocalAuth()?.region === REGIONS.IN

  const sip: BuildOption = {
    value: 'sip',
    title: 'Connect Your Existing Voice Agent',
    description: isIndia
      ? 'Already running an agent on ElevenLabs, LiveKit, or your own stack? Give it phone connectivity over SIP.'
      : 'Already running an agent on Vapi, Retell, ElevenLabs, OpenAI, LiveKit, or your own stack? Give it phone connectivity over SIP.',
    badge: { label: 'SIP Trunking', tone: 'neutral' },
  }

  const messaging: BuildOption = {
    value: 'messaging',
    title: 'Messaging',
    // India gets WhatsApp only: SMS is not offered there.
    description: isIndia
      ? 'Send OTPs, alerts, and campaigns over WhatsApp, at scale.'
      : 'Send OTPs, alerts, and campaigns over SMS and WhatsApp, at scale.',
    badge: {
      label: 'Enterprise Plan',
      tone: 'enterprise',
      tooltip: 'Messaging is available on Enterprise. Talk to sales.',
    },
  }

  // India has no no-code option and puts audio streaming ahead of SIP.
  return isIndia ? [AUDIO, sip, VOICE_API, messaging] : [VOICE_AI, sip, AUDIO, VOICE_API, messaging]
}

export interface FrameworkOption {
  value: Framework
  label: string
  // When set, selecting this option reveals a free-text field with this
  // placeholder so we can capture the user's specific stack.
  detailPlaceholder?: string
}

// Always last on both lists: it is the fallback for everything above it.
const CUSTOM_STACK: FrameworkOption = {
  value: 'custom',
  label: 'Custom stack',
  detailPlaceholder: 'What are you running? e.g. in-house, Vapi, Retell, Ultravox…',
}

// Popular platforms we don't compete with (so no Retell, Vapi or ElevenLabs),
// listed on the transport each one actually supports.
const SIP_FRAMEWORKS: FrameworkOption[] = [
  // Labels match the Create Trunk platform picker the SIP path opens next.
  { value: 'livekit', label: 'LiveKit Cloud' },
  { value: 'openai', label: 'OpenAI Realtime' },
  { value: 'xai', label: 'xAI (SpaceXAI)' },
  CUSTOM_STACK,
]

const AUDIO_FRAMEWORKS: FrameworkOption[] = [
  { value: 'pipecat', label: 'Pipecat' },
  // Gemini Live is WebSocket-based, so it belongs on audio streaming only.
  { value: 'gemini', label: 'Gemini Live' },
  CUSTOM_STACK,
]

export const frameworkOptions = (build: BuildChoice | ''): FrameworkOption[] =>
  build === 'audio' ? AUDIO_FRAMEWORKS : SIP_FRAMEWORKS

export interface StepProps {
  state: WizardState
  update: (patch: Partial<WizardState>) => void
}

// Voice API connects no agent framework, so it gets no probe.
export const needsFrameworkStep = (build: BuildChoice | ''): boolean =>
  build === 'sip' || build === 'audio'

// Lands on the plain product page so its onboarding banner is the first thing seen.
export const resolveDestination = (state: WizardState): string => {
  switch (state.build) {
    case 'sip':
      return '/sip-trunking/inbound'
    // Both configure their answer_url on Voice Applications.
    case 'audio':
    case 'voice-api':
      return '/applications'
    case 'messaging':
      // /compliance/* is not registered for India orgs, so it would 404.
      return getLocalAuth()?.region === REGIONS.IN ? '/whatsapp/numbers' : '/compliance/drafts'
    default:
      return '/agents'
  }
}

// Kept out of the component so it can be tested without useProfileQuery (CLAUDE.md failure mode 17).
export const isComplete = (state: WizardState): boolean => {
  if (!state.build) return false
  // The agent description is optional qualification data, never a gate.
  return needsFrameworkStep(state.build) ? !!state.framework : true
}

// Cleaned at submit, never on change (CLAUDE.md failure mode 21).
const cleanFreeText = (value: string): string | undefined =>
  stripUnsafeChars(value).trim() || undefined

export const BUILD_TO_USE_CASE: Record<BuildChoice, BuildGoal> = {
  'voice-ai': BuildGoal.VOICE_AI_AGENTS,
  sip: BuildGoal.SIP_TRUNKING,
  audio: BuildGoal.AUDIO_STREAMING,
  'voice-api': BuildGoal.VOICE_API,
  messaging: BuildGoal.MESSAGING,
}

// Free text is stripped of angle brackets (core rejects them) but never truncated.
export const buildOnboardingPayload = (
  state: WizardState,
  businessType: string,
): OnboardingFormData => ({
  business_type: businessType || undefined,
  use_case: state.build ? BUILD_TO_USE_CASE[state.build] : undefined,
  build: state.build || undefined,
  framework: state.framework || undefined,
  framework_detail: cleanFreeText(state.frameworkDetail),
  agent_description: state.build === 'voice-ai' ? cleanFreeText(state.agentDescription) : undefined,
})
